// Die Meldungs-Logik: was eine Meldung IST, wie aus Roh-Ereignissen
// Zustände und Episoden werden, und wo eine Meldung hinführt.
//
// Bewusst OHNE Runes und ohne Store-Zugriff -- alles hier sind reine
// Funktionen über übergebenen Daten. Zwei Gründe:
//
//  1. Testbarkeit. Die Paarung von Störung und Entwarnung ist die
//     kniffligste Stelle des Meldungssystems (Zeitachse, Episoden ohne
//     Anfang, Wiederholungen ohne Entwarnung dazwischen). Sie lief bis
//     23.08.2026 ganz ohne Test. Als reines Modul läuft sie unter
//     `node --test`, ohne Browser und ohne Svelte-Laufzeit.
//  2. EIN Ort für das Paar-Wissen. Vorher lag es verstreut im Store;
//     wer ein Paar ergänzt, muss alle Berührpunkte auf einmal sehen.
//
// Der Store (alerts-store.svelte.ts) behält, was Zustand hat: Laden,
// Acks, WS-Anbindung, die abgeleiteten Domain-Alerts.

import type { AppEvent } from "./types";

export type AlertSeverity = "info" | "warning" | "problem" | "critical";

export interface Alert {
  id: string;
  severity: AlertSeverity;
  title: string;
  detail?: string;
  ts?: string;
  /** Ziel-ROUTE, ohne '#' -- also "/system/tasks", wie ueberall sonst in
   *  der App (routes.ts, NavSearchPanel, SectionHub). Wer daraus einen
   *  Link baut, setzt das '#' selbst davor; wer einen Tab oeffnet, gibt
   *  den Pfad unveraendert weiter.
   *
   *  Bis 16.08. stand hier ein fertiger Hash-Link ("#/system/tasks").
   *  Das ging gut, solange die Meldung nur in einem <a href> landete --
   *  seit sie im unteren Panel einen TAB oeffnet, entstand daraus ein
   *  Tab auf den Pfad "#/system/tasks", und der ist keine Route: 404. */
  href: string;
  eventId?: number;
  // domain: Event-Domain des zugrundeliegenden events-Eintrags (nur bei
  // Event-Alerts gesetzt). Die Perspektiven-Ampel (perspective-status)
  // ordnet damit Zuhause-Domains ohne eigene Zielseite (door, motion, …)
  // der richtigen Welt zu -- der href zeigt dort nur aufs Audit-Log.
  domain?: string;
  /** eventType: der Ereignis-Typ ("device_offline"). Das Detail sucht
   *  damit die VORIGE Episode desselben Dings (s. zuvorZurueck). */
  eventType?: string;
  /** seit: Beginn der Störung. Nur gesetzt, wenn der Server das Ereignis
   *  als Zustand führt (audit.IstZustand) -- bei einem einmaligen
   *  Ereignis wäre ein Zeitraum eine Erfindung. Das Detail macht daraus
   *  "nicht erreichbar seit 21:02 · seit 1 h 42". */
  seit?: string;
  /** zuvorZurueck: das Ende der VORIGEN Episode desselben Dings. Sagt,
   *  wie lange es zwischendurch gut lief -- der Unterschied zwischen
   *  "fällt dauernd aus" und "einmalig". Lädt AlertDetail bei Bedarf
   *  nach; im Ereignis selbst steht es nicht. */
  zuvorZurueck?: string;
  /** bis: Ende einer abgeschlossenen Störung (nur im Verlauf, s.
   *  fasseVerlauf). Fehlt es bei gesetztem `seit`, läuft sie noch. */
  bis?: string;
  /** zuletzt: wann die noch laufende Störung zuletzt beobachtet wurde.
   *  Nur bei zusammengefassten Warnungen gesetzt und nur dann von
   *  `seit` verschieden. Beantwortet "steht das noch, oder hat es bloß
   *  niemand aufgeräumt" — ohne dass die Meldung dafür so tun muss, als
   *  wäre sie neu. */
  zuletzt?: string;
  /** selbstErledigt: die Störung ist vorbei, ohne dass jemand etwas
   *  wegklicken musste — das Gerät meldete sich zurück, der Task lief
   *  wieder durch. Zwei Quellen: ein Entwarnungs-Ereignis (Zustands-Paar)
   *  oder der Auto-Ack des Servers (`acked_by = "auto:…"`, gesetzt von
   *  audit.Emit-Recovery).
   *
   *  Der Unterschied zu `acked` ist der Punkt: „gelesen" heißt, ein
   *  Mensch hat es zur Kenntnis genommen; „selbst erledigt" heißt, es
   *  hat sich geklärt. Im Rückblick will man das auseinanderhalten
   *  können (Tim 23.08.) — im Aktiv-Tab und am Warn-Dreieck taucht so
   *  eine Meldung dagegen NIE auf. */
  selbstErledigt?: boolean;
}

// ── Dringlichkeit ────────────────────────────────────────────────────────────
//
// Rang UND Beschriftung standen bis 23.08.2026 dreifach da: eine
// rank-Map im Store (Sortierung), sevRank/sevLabel auf der Meldungen-
// Seite, dazu die Übersetzung in eventToAlert. Drei Orte für dieselbe
// Aussage sind zwei zu viel -- eine Severity mehr hätte an zwei Stellen
// still gefehlt.

/** Kleiner Rang = dringender. Sortiert Popover und Tabelle. */
export const SEVERITY_RANG: Record<AlertSeverity, number> = {
  critical: 0,
  problem: 1,
  warning: 2,
  info: 3,
};

export function severityRang(s: AlertSeverity): number {
  return SEVERITY_RANG[s];
}

/** Übersetzungs-Schlüssel je Severity. Bewusst nur der Schlüssel, nicht
 *  der fertige Text: t() gehört an die Aufrufstelle (i18n-Mandat), damit
 *  die Sprachumschaltung die Komponente neu auswertet. */
export const SEVERITY_TEXT: Record<AlertSeverity, { key: string; fallback: string }> = {
  critical: { key: "alerts.sev.critical", fallback: "Kritisch" },
  problem: { key: "alerts.sev.problem", fallback: "Problem" },
  warning: { key: "alerts.sev.warning", fallback: "Warnung" },
  info: { key: "alerts.sev.info", fallback: "Hinweis" },
};

/** Farbton je Severity für StatusBadge und Symbole. Kritisch und Problem
 *  sind beide rot -- was sie trennt, ist nicht die Farbe, sondern dass
 *  sich Kritisches nicht wegklicken lässt (s. quittierbar). */
export const SEVERITY_TON: Record<AlertSeverity, "danger" | "warn" | "info"> = {
  critical: "danger",
  problem: "danger",
  warning: "warn",
  info: "info",
};

/** severityVonEvent übersetzt die Backend-Severity (info/warn/error/
 *  critical) in die Anzeige-Severity. */
export function severityVonEvent(evSeverity: string): AlertSeverity {
  if (evSeverity === "critical") return "critical";
  if (evSeverity === "error") return "problem";
  if (evSeverity === "warn") return "warning";
  return "info";
}

/** quittierbar: lässt sich die Meldung als gelesen wegklicken?
 *
 *  Kritisches nicht. Der Server lehnt den Ack mit 403 ab
 *  (internal/api/events.go); eine kritische Meldung verschwindet nur,
 *  wenn ihre Ursache behoben ist -- der Pool wieder vollständig, der
 *  Host wieder da. Ein "gelesen"-Knopf daran wäre ein toter Klick. */
export function quittierbar(a: Alert): boolean {
  return a.severity !== "critical";
}

/** Wie das Detail einen Zustand benennt. „Weg seit … wieder da" stimmt
 *  nur für etwas, das nicht erreichbar ist; ein gerissenes Backup, eine
 *  rote Prüfung oder eine neue SolidCraft-Version hat Anfang und Ende,
 *  ist aber nicht weg (Tim 24.10.2026: die SolidCraft-Meldung stand als
 *  „Weg seit 07:54" da). */
export type ZustandsArt = "task" | "speicher" | "weg" | "allgemein";

export function zustandsArt(a: Alert): ZustandsArt {
  if (a.id.startsWith("task-fail") || a.domain === "tasks") return "task";
  if (a.domain === "storage") return "speicher";
  if (/offline|unreachable|outage|container_down|mac_stale/.test(a.eventType ?? "")) return "weg";
  return "allgemein";
}

export function alertFingerprint(a: Alert): string {
  // `ts` MUSS mit hinein. Ohne ihn beschreibt der Fingerabdruck nur die
  // ART der Meldung, nicht den VORFALL -- und ein Ack galt damit fuer
  // alle kuenftigen Vorfälle derselben Art.
  //
  // Konkret (Tim, 01.09.2026): "WOB Media Backup" ist am 30.08.
  // fehlgeschlagen und wurde weggeklickt. Der Fingerabdruck war
  // `warning|Task fehlgeschlagen|WOB Media Backup` -- der Name des Tasks
  // aendert sich nie. Als derselbe Task am 01.09. wieder scheiterte,
  // passte der alte Ack noch, und die Meldung erschien NIE WIEDER. Drei
  // solcher Dauer-Stummschaltungen standen in alert_acks.
  //
  // Mit `ts` gilt der Ack fuer genau die Episode, die man gelesen hat.
  // Das passt zu `ts` = ANFANG der Stoerung (audit.Emit bumpt seit dem
  // 31.08. nur noch last_seen): eine anhaltende Stoerung behaelt ihren
  // Zeitstempel und bleibt weggeklickt, eine NEUE bekommt einen neuen.
  return `${a.severity}|${a.title}|${a.detail ?? ""}|${a.ts ?? ""}`;
}

// ── Synthetische Meldungen: die Regel für "gesehen" ──────────────────────────
//
// Sechs Quellen bauen Meldungen aus Domain-Summaries statt aus der
// events-Tabelle (wan, hosts, sec-attempts, dns, klima-*, task-fail-*).
// Für sie alle gilt EINE Regel:
//
//   Steht hinter der Meldung ein echtes Ereignis, MUSS sie dessen
//   `eventId` tragen.
//
// Denn `ackAlert` entscheidet daran, wohin das "gesehen" geht: mit
// eventId an den SERVER (events.acked_at), ohne sie in den lokalen
// alert_acks-Topf. Fehlt die ID, entstehen zwei Wahrheiten — genau das
// ist am 01.09.2026 passiert: die Meldung war gelesen, die
// Dashboard-Karte stand weiter auf rot.
//
// Heute ist `task-fail-*` die einzige Quelle mit Ereignis dahinter; die
// übrigen fünf leiten sich aus Live-Zustand ab und haben keins. Wer
// eine weitere Quelle anschließt, beantwortet zuerst diese Frage.
// (Aktive Bans waren bis 04.10.2026 eine siebte: jetzt ist der Ban selbst
// der Hinweis -- ein Ereignis mit eventId.)
//
// Die Abbildung steht DARUM hier und nicht im Store: hier ist sie eine
// reine Funktion und damit prüfbar, ohne Svelte-Laufzeit.

/** Die Felder aus tasks-summary, die eine Meldung braucht. Bewusst als
 *  eigene Form statt Import aus dem Runes-Modul — dieses Modul bleibt
 *  frei von Store-Abhängigkeiten (siehe Kopf der Datei). */
export interface FehlgeschlagenerTask {
  entity: string;
  name: string;
  ts?: string;
  event_id?: number;
}

/** taskFailAlert baut die Meldung zu einem aktuell fehlgeschlagenen
 *  Task. Siehe die Regel oben: `eventId` ist NICHT optional-aus-Bequem-
 *  lichkeit, sondern das, was Meldungen, Dashboard-Karte und Tasks-Seite
 *  auf denselben Stand bringt. */
export function taskFailAlert(f: FehlgeschlagenerTask): Alert {
  return {
    id: `task-fail-${f.entity}`,
    severity: "warning",
    title: "Task fehlgeschlagen",
    detail: f.name,
    // ts + domain füllen im Popover die Zeit- und auf der Meldungen-
    // Seite die Bereich-Spalte -- sonst stand dort "—", obwohl die
    // Summary den Zeitpunkt kennt.
    ts: f.ts,
    domain: "tasks",
    href: "/system/tasks",
    eventId: f.event_id,
  };
}

// ── Was NICHT in die Meldungs-Liste gehört ───────────────────────────────────

/** ROUTINE_TYPE_EXCLUDE: Ereignisse, die NIE eine Meldung sind -- weder
 *  als offene Aufgabe noch im Rückblick. Routine, Entwarnungen und was
 *  der Nutzer gerade selbst getan hat. Im Audit-Log stehen sie
 *  vollständig; nur aus der Meldungs-Liste bleiben sie draußen.
 *
 *  Als Liste (nicht als fertiger CSV-String), damit die Wächter-Tests
 *  einzelne Typen prüfen können. */
export const ROUTINE_TYPE_EXCLUDE: string[] = [
  "task_finished", "deploy_ok", "snapshot_restored",
  "dataset_created", "unban", "found",
  "routable", "carrier", "dormant", "no-carrier", "off", "degraded",
  // Automation-Firings sind reine Historie (Detail-Overlay), keine
  // Warnungen -- gleiche Kategorie wie task_finished.
  "rule_fired",
  // ── Entwarnungen ───────────────────────────────────────────────────
  //
  // Eine gute Nachricht ist keine Aufgabe. „Light Storage wieder
  // erreichbar" als offener Hinweis, den man wegklicken muss, war Tims
  // Ausgangsbeschwerde am 21.08. -- und genau das kam am 23.08. für ein
  // Deploy lang zurück, als die Frontend-Paarung wegfiel, die sie bis
  // dahin verschluckt hatte, das Laden aber blieb. Neun Stück standen
  // sofort im Aktiv-Tab.
  //
  // Die Entwarnung braucht die Liste nicht: sie hat ihre Arbeit schon
  // getan, als der Server die zugehörige Störung damit geschlossen hat
  // (RecoveryType). Im Rückblick steht sie als „Wieder da" IM Detail der
  // Störung, zu der sie gehört.
  "device_online", "push_delivery_ok", "power_recovered", "wan_recovery",
  "mac_healthy", "backup_ok",
  // Vault-Löschbremse gelöst (von selbst oder am Mac freigegeben): schließt
  // die Meldung „loeschbremse" und steht im Audit-Log.
  "loeschbremse_frei",
  // ── Was der Nutzer selbst getan hat ────────────────────────────────
  //
  // Meldungen sind fuer Dinge, die STILL passieren und die man sonst
  // nicht mitbekaeme (Tim, 29.08.: "bei den Meldungen geht es ja darum
  // dinge zu sehen, die sonst still passieren"). Wer gerade selbst die
  // Farbe einer Backup-Gruppe geaendert hat, weiss das -- ihm das als
  // offene Meldung hinzulegen, die er wegklicken muss, macht die Liste
  // zu einer Quittungsrolle. Sechs "Backup-Gruppe geaendert (farbe)"
  // standen prompt im Aktiv-Tab.
  //
  // Weg ist hier NUR die Meldung: als Ereignis bleibt jede dieser
  // Aenderungen vollstaendig im Audit-Log (domain=backups). Die Frage
  // "wer hat wann was umgestellt" muss beantwortbar bleiben -- sie
  // gehoert nur nicht auf den Stapel der offenen Aufgaben.
  //
  // Was NICHT hierher gehoert: alles, was HINTERHER und unbeobachtet
  // passiert. backup_data_destroy_failed etwa laeuft in einer Goroutine
  // lange nach der Antwort -- genau die Sorte stiller Fehlschlag, fuer
  // die es die Liste gibt.
  "group_created", "group_edited", "group_deleted", "chain_group_changed",
  "chain_created", "chain_deleted", "chain_stage_added", "chain_stage_edited",
  "chain_stage_removed", "chain_verify_planned", "chain_verify_removed",
  "backup_data_destroyed",
  // Eine Datei aus einem Backup-Stand zu laden ist ebenfalls etwas, das
  // der Nutzer gerade selbst getan hat -- und es aendert nichts. Im
  // Audit-Log bleibt es (dort ist es wichtig: hier verlaesst Inhalt aus
  // einem Backup den Host), auf dem Stapel offener Aufgaben hat es
  // nichts zu suchen.
  "backup_file_downloaded",
  // Eine Konstruktion in den Papierkorb legen, zurückholen oder endgültig
  // löschen tut der Nutzer selbst (internal/konstruktionen/papierkorb.go).
  // Die Meldung „liegt seit 30 Tagen im Papierkorb" (papierkorb_alt)
  // bleibt -- die kommt still.
  "konstruktion_papierkorb", "konstruktion_wiederhergestellt", "konstruktion_endgueltig_geloescht",
  // Aus Fusion uebernommen: angestossen hat es der Nutzer (fusion.go).
  "konstruktion_aus_fusion",
  // In der SolidCraft-Oberfläche gespeichert: hat der Nutzer selbst getan
  // (oberflaeche.go); „Skript nachziehen" steht an der Konstruktion.
  "konstruktion_aus_oberflaeche",
  // Befund abgehakt / wieder offen: hat der Nutzer selbst getan (abhaken.go).
  "konstruktion_befund_abgehakt", "konstruktion_befund_wieder_offen",
  // Kaufteile anlegen, ändern, entfernen tut der Nutzer selbst; die
  // einmalige Übernahme der alten Kaufteile.yaml ist Routine (kaufteile.go).
  "kaufteil_neu", "kaufteil_geaendert", "kaufteil_entfernt", "kaufteile_uebernommen",
  // Termine eintragen, ändern, löschen tut Tim selbst oder Claude auf seine
  // Bitte (internal/kalender). Die Erinnerung selbst (termin_erinnerung)
  // bleibt -- sie ist der Zweck des Kalenders.
  "termin_angelegt", "termin_geaendert", "termin_geloescht", "termin_weiter",
  // Der Druckerwächter hat einen Verdacht nach der zweiten Meinung
  // verworfen (internal/druckwaechter): Routine, der Fall steht mit Bild
  // auf der Drucker-Seite. Zweimal in Folge verworfen meldet er selbst.
  "druck_waechter_verworfen",
  // Die taegliche Pflege ist gewollte Routine ("12 Staende ueber der
  // Staffel entfernt"). Ihr GEGENSTUECK backup_thin_failed bleibt in der
  // Liste -- das passiert hinterher und unbeobachtet.
  "backup_thinned",
  // Das Zigbee-Netz zum Anlernen öffnen oder vorzeitig schließen tut der
  // Nutzer selbst, mit dem Countdown vor Augen -- dieselbe Kategorie wie
  // oben „was der Nutzer selbst getan hat". Beim ersten Einsatz am 11.09.
  // standen prompt fünf davon im Aktiv-Tab. Im Audit-Log (domain=zigbee)
  // bleiben sie: wann das Netz für Fremdgeräte offen stand, muss
  // nachvollziehbar sein. Das stille Auslaufen des Fensters wird gar nicht
  // erst protokolliert.
  "zigbee_permit_join_opened", "zigbee_permit_join_closed",
  // Ein Gerät zu entfernen ist ebenso eine eigene Handlung, mit Rückfrage
  // davor -- welche Automatiken daran hingen, steht im Audit-Log.
  "zigbee_device_removed", "zigbee_device_renamed",
  // Eine Stellgröße am Funkmelder ändern (Schwelle, Haltezeit, LED …)
  // oder ihn neu kalibrieren: der Nutzer sitzt davor und sieht die
  // Wirkung an der Kurve. Im Audit-Log (domain=funkmelder) bleibt es mit
  // vorher und nachher -- „warum reagiert der Melder seit Dienstag
  // träger" muss beantwortbar sein.
  "funkmelder_einstellung_geaendert", "funkmelder_kalibrierung_gestartet",
  // Das Funkbild angesehen (die Erkennung war so lange pausiert): der
  // Nutzer saß davor. Im Audit-Log steht es mit der Dauer.
  "funkmelder_rohdaten_angesehen",
  // Danach die Schwelle auf den Wert von vorher gestellt: aufgeräumt,
  // nichts zu tun. (Gelingt es NICHT, kommt ein anderer Typ als Warnung.)
  "funkmelder_schwelle_zurueckgestellt",
  // Eine Kalibrierung im Trubel verworfen, eine feste Schwelle nach dem
  // Neustart nachgesetzt: der Server hat aufgeräumt. (Eine unplausible
  // Kalibrierung OHNE Rückfall ist eine Warnung und bleibt Meldung.)
  "funkmelder_kalibrierung_verworfen", "funkmelder_schwelle_nachgesetzt",
  // Der Kalibrier-Auftrag des Nutzers ist gelungen: das Ergebnis steht an
  // der Schwelle. (NICHT gelungen -- keine Ruhe, verworfen -- ist
  // funkmelder_kalibrierung_nicht_erfolgt und bleibt ein Hinweis: er hat
  // den Knopf gedrückt und ist gegangen, er soll es erfahren.)
  "funkmelder_kalibriert",
  // Die Funkmelder-Funktion eines Geräts an- oder abgeschaltet: der Nutzer
  // hat es gerade selbst getan. Und nach einem Neustart des Geräts erneut
  // abgeschaltet: der Server hat aufgeräumt. (Lässt es sich NICHT
  // abschalten, kommt funkmelder_nicht_abgeschaltet als Warnung.)
  "funkmelder_funktion_geschaltet", "funkmelder_erneut_abgeschaltet",
  // Einen Task anzulegen oder zu löschen tut der Nutzer selbst, mit
  // Rückfrage vor dem Löschen. Im Audit-Log (domain=tasks) bleibt beides
  // -- „wer hat diesen Task angelegt" muss beantwortbar sein.
  "task_created", "task_deleted",
  // Den Tausch einer Pool-Platte startet der Nutzer selbst, nach Rückfrage
  // -- ob er klappt, sieht er sofort (Toast, Fortschritt am Pool). Offen
  // bleibt, was still weiterläuft: der Pool ist beeinträchtigt, bis der
  // Resilver durch ist (pool_degraded).
  "pool_replace_started", "pool_replace_failed",
  // Gruppen anlegen, umbenennen, auflösen und besetzen ist ebenfalls
  // eigene Handarbeit mit dem Ergebnis vor Augen. Im Audit-Log
  // (domain=zigbee) bleibt es -- gerade beim Umbenennen steht dort, welche
  // Automatiken mitgezogen wurden.
  "zigbee_group_created", "zigbee_group_removed",
  "zigbee_group_renamed", "zigbee_group_members_changed",
  // Server-Seite (15.10.2026): den Dienst neu starten, den Neustart
  // zurücknehmen -- der Nutzer sitzt davor. Und jeder Start des Dienstes
  // (Deploy, Neustart, Rechner-Neustart) ist Routine; im Audit-Log
  // (domain=server) bleibt er mit Grund und Version. Meldung bleiben:
  // dienst_absturz (systemd musste neu starten), dienst_neustart_ausgefallen
  // (nach 10 min lief noch etwas -- der Nutzer ist vielleicht längst
  // weg), dienst_neustart_fehlgeschlagen.
  "dienst_gestartet", "dienst_neustart_angefordert", "dienst_neustart", "dienst_neustart_abgebrochen",
  // 3D-Drucker (15.10.2026): ein Druck beginnt, jemand bricht ihn selbst ab
  // oder schaltet Licht/Pause -- im Audit-Log (domain=drucker) bleibt es,
  // der Verlauf der Drucker-Seite baut daraus. Meldung bleiben:
  // druck_fertig (Hinweis: kann vom Bett), druck_gescheitert,
  // druck_angehalten, drucker_hms.
  "druck_gestartet", "druck_abgebrochen", "drucker_gesteuert",
  // Claude-Chat (16.10.2026): Freigaben erteilen/ablehnen, Regeln anlegen,
  // Chats anlegen/wegräumen tut der Nutzer im Chat selbst -- „es macht
  // keinen Sinn, hier zu arbeiten und dann einen Hinweis zu bekommen"
  // (Tim). Im Audit-Log (domain=claude) bleibt jede Freigabe mit Befehl.
  // Wartet ein Chat auf ihn, kommt ein Push (internal/api/claude.go).
  "freigabe_erteilt", "freigabe_abgelehnt", "regel_angelegt", "chat_angelegt", "chat_geloescht",
  "freigabe_in_app",
  "freigaben_geaendert",
  // Claude Code neu angemeldet: Tim hat den Code selbst eingegeben.
  "anmeldung_erneuert",
  // DNS-Seite (24.10.2026): Schutz pausieren und wieder an, eine Domain
  // freigeben oder sperren, Filterlisten aktualisieren -- der Nutzer hat
  // es gerade selbst getan, mit Vault-PIN. Im Audit-Log (domain=dns)
  // bleibt jeder Handgriff mit Regel und Akteur; „wer hat diese Domain
  // freigegeben" muss beantwortbar sein. Meldung bleibt: dns_outage.
  "dns_schutz_pausiert", "dns_schutz_an", "dns_domain_freigegeben", "dns_domain_gesperrt",
  "dns_listen_aktualisiert",
  // netsec (25.10.2026): das Anlernen eines Routers starten oder beenden
  // tut der Nutzer selbst, mit Vault-PIN. Im Audit-Log (domain=netsec)
  // bleibt es -- „warum kam für dieses Gerät keine Meldung" muss
  // beantwortbar sein. Meldung bleiben: new_device, router_neu.
  "netsec_anlernen", "netsec_anlernen_beendet",
];

/** DOPPEL_TYPE_EXCLUDE: Ereignisse, die sehr wohl eine Meldung sind --
 *  aber im AKTIV-Tab bereits von einem synthetischen Alert vertreten
 *  werden, der denselben Zustand aus einer Live-Summary ableitet:
 *
 *    task_failed  ->  "task-fail-<entity>" (aus tasks-summary)
 *    wan_outage   ->  "wan" / "wan:<router>" (aus wan-summary, je Router)
 *    wan_stumm    ->  "wan:<router>"       (aus wan-summary: ein Router,
 *                                           dessen Internet forge nicht
 *                                           selbst prüft, meldet sich nicht)
 *    dns_outage   ->  "dns"                (aus dns-health)
 *
 *  Ohne diesen Ausschluss stuende jede Stoerung im Aktiv-Tab zweimal.
 *
 *  Im VERLAUF ist es umgekehrt: dort gibt es die synthetischen Alerts
 *  nicht (sie kennen nur das Jetzt, keine Vergangenheit) -- das Ereignis
 *  IST der Rueckblick. Es darf dort also nicht ausgeschlossen werden;
 *  sonst waere der Verlauf ausgerechnet um die fehlgeschlagenen Tasks
 *  aermer, deretwegen man ihn aufschlaegt. */
export const DOPPEL_TYPE_EXCLUDE: string[] = [
  "task_failed", "wan_outage", "wan_stumm", "dns_outage",
];

/** Was im Aktiv-Tab nicht auftaucht: Routine PLUS die vom synthetischen
 *  Alert schon vertretenen Typen. */
export const BANNER_TYPE_EXCLUDE: string[] = [
  ...ROUTINE_TYPE_EXCLUDE,
  ...DOPPEL_TYPE_EXCLUDE,
];

/** Dieselbe Liste als Query-Parameter. */
export const BANNER_TYPE_EXCLUDE_CSV = BANNER_TYPE_EXCLUDE.join(",");

/** Was im Verlauf nicht auftaucht: NUR die Routine. Der Rueckblick zeigt
 *  jede Meldung, die es je gab -- Hinweis, Warnung, Problem -- gelesen
 *  oder nicht. */
export const VERLAUF_TYPE_EXCLUDE_CSV = ROUTINE_TYPE_EXCLUDE.join(",");

// ── Zustand statt Flanke ─────────────────────────────────────────────────────
//
// Eine Störung und ihre Entwarnung sind EIN Ding in zwei Momenten, nicht
// zwei Meldungen. Tim am 21.08.: „Es ist dann nicht ein off und ein on
// Hinweis sondern nur ein Hinweis zum Zustand hier der Funsel."
//
// Wer das weiß, ist der SERVER. Er ist der Ort, an dem die Recovery
// passiert (`RecoveryType`/`RecoveryTypes`, `AckOpenByEntity`), und er
// stempelt dabei `acked_by = "auto:…"` und `acked_at` = Zeitpunkt der
// Heilung. Damit trägt das Störungs-Ereignis sein eigenes Ende:
//
//     Episode = ts … acked_at,  wenn acked_by mit "auto:" beginnt
//     läuft noch = kein acked_at
//
// Bis 23.08.2026 führte diese Datei zusätzlich eine eigene Paar-Liste
// (ZUSTANDS_PAARE) und der Verlauf lud die Entwarnungs-Ereignisse eigens
// nach, um sie selbst zu paaren. Das war ein zweites, schwächeres
// Register neben dem des Servers -- vier Berührpunkte in zwei Sprachen,
// von denen man zwei still falsch machen konnte. Es ist ersatzlos weg;
// die Entwarnungen werden gar nicht mehr geholt, also stehen sie auch
// nicht mehr als eigene Zeile herum.
//
// Was der Server zusätzlich mitschickt, ist `zustand`: ob ein noch
// OFFENES Ereignis überhaupt einen Anfang hat, den man zeigen darf. Ein
// „weg seit 09:12" bei einem aufgedeckten Geheimnis wäre eine Erfindung.
// Quelle ist das Register in internal/audit (audit.IstZustand).

// ── Vom Ereignis zur Meldung ─────────────────────────────────────────────────

// eventHref bestimmt den "Ereignisort" eines events-Alerts: die Seite,
// auf der das Ereignis herkommt UND gehandelt werden kann. Wo die Zielseite
// einen ?focus=<entity>-Deep-Link versteht (netsec, agents), haengen wir
// die entity an, damit direkt der betroffene Eintrag markiert/angesprungen
// wird. Unbekannte Domains landen im Audit-Log (/system/events) statt
// stumpf auf dem Dashboard -- dort ist jedes Ereignis nachvollziehbar.
//
// Die Ziele sind echte Routen aus routes.ts -- dass sie das bleiben,
// prüft meldungs-logik.test.mjs (das Gegenstück zu internal/navcheck,
// das dasselbe für die Push-URLs der Go-Seite tut).
export function eventHref(ev: AppEvent): string {
  const focus = ev.entity ? `?focus=${encodeURIComponent(ev.entity)}` : "";
  switch (ev.domain) {
    case "netsec":   return `/sicherheit/netsec${focus}`;
    // WAN-Outage-Events (wan:probe) -> Netzwerk-Page, wo die Internet-
    // Erreichbarkeit lebt (gleicher Ort wie der synthetische WAN-Alert).
    case "network":  return "/infra/network";
    case "dns":      return "/sicherheit/dns";
    // Geraete-Ausfaelle (device_offline): auf der Geraete-Seite steht
    // der Stoerungs-Banner mit "Zeile zeigen" zum betroffenen Geraet.
    case "devices":  return "/zuhause/geraete";
    case "agents":   return `/infra/agents${focus}`;
    // Die beiden Archiv-Aufgaben (Sicherung, Handgriffe) zeigen auf die
    // Seite, auf der man sieht, was gesichert ist und was nicht.
    case "tasks":    return (ev.entity ?? "").startsWith("task:archiv-") ? "/system/archiv" : "/system/tasks";
    // Archiv gelöscht, zurückgeholt, Rest übernommen (docs/archiv.md).
    case "archiv":   return "/system/archiv";
    // Befund der Wiederherstellungsprobe (probe_befund): die Seite „Proben"
    // zeigt die Kette mit ihren abweichenden Dateien; alles andere aus der
    // Backup-Domain gehört zu den Ketten.
    case "backups":  return ev.type.startsWith("probe_") ? "/system/backups/proben" : ev.type.startsWith("abdeckung_") ? "/system/backups/abdeckung" : "/system/backups";
    // Sicherheits-Uebersicht heisst /security (nicht /sicherheit/security
    // -- das war bis 23.08.2026 ein toter Link ohne Redirect, also ein
    // 404 fuer jede fail2ban-Meldung).
    case "fail2ban": return "/security";
    case "docker":   return "/infra/docker";
    // broker_unreachable: das Dashboard hat keine Verbindung zum Broker --
    // Verbindung und Zugang stehen auf der MQTT-Seite.
    case "mqtt":     return "/einstellungen?auf=s.mqtt";
    case "storage":  return "/infra/storage";
    // door_open_long: die Haustür steht zu lange offen -- die Türen stehen
    // bei den Sensoren.
    case "home":     return "/zuhause/sensoren";
    case "netzfrequenz": return "/infra/netzfrequenz";
    // Druck fertig/gescheitert/angehalten, Druckerfehler (internal/bambu).
    // Fällige Wartung (Entity <serie>:<punkt>): die Seite klappt die Zeile
    // des Punkts auf.
    case "drucker":  return ev.type === "wartung_faellig" && ev.entity?.includes(":")
      ? `/werkstatt/drucker?wartung=${encodeURIComponent(ev.entity.slice(ev.entity.lastIndexOf(":") + 1))}`
      : "/werkstatt/drucker";
    // pruefung_rot: ein Stand des Repos tim/konstruktionen ist rot
    // (internal/konstruktionen).
    case "konstruktionen": return "/werkstatt/konstruktionen";
    // Training fertig/gescheitert, Kosten-Obergrenze; Umwandlung für den
    // Hailo fertig/gescheitert (internal/argus).
    case "argus":    return ev.type.startsWith("hailo_") ? "/argus/modelle" : "/argus/training";
    case "link":     return "/infra/status";
    // cluster_unreachable: Link-Stoerung einer Insel -- der Zustand
    // ist auf der Status-Page (Verbindungen) ablesbar.
    case "cluster":  return "/infra/status";
    // "host" UND "hosts": das Register in internal/audit führt die
    // Domain im Plural (host_offline, hosts_updated), diese Liste stand
    // im Singular — jede hosts-Meldung landete damit stumpf im
    // Audit-Log statt auf der Cluster-Seite (31.08.2026).
    case "host":
    case "hosts":    return "/infra/cluster";
    default:         return "/system/events";
  }
}

// eventToAlert mappt eine events-Zeile aufs Alert-Shape. Exportiert, damit
// die Meldungen-Seite (Verlauf-Tab) dieselbe Zuordnung nutzt wie der
// Store -- gleiche Severity-Uebersetzung, gleicher Ziel-Link, und
// damit dasselbe AlertDetail-Overlay funktioniert.
export function eventToAlert(ev: AppEvent): Alert {
  // Auto-Ack des Servers = die Sache hat sich selbst erledigt, und
  // acked_at ist der Zeitpunkt DER HEILUNG (audit.Emit stempelt ihn beim
  // Recovery-Ereignis). Damit trägt das Fehler-Ereignis sein eigenes
  // Ende — der Verlauf braucht dafür keine zweite Abfrage nach dem
  // Entwarnungs-Ereignis. Das ist bei Tasks der Unterschied zwischen
  // "eine Zeile mehr" und "18.000 task_finished mitladen".
  const selbst = !!ev.acked_at && (ev.acked_by ?? "").startsWith("auto:");
  const alert: Alert = {
    id: `event-${ev.id}`,
    eventId: ev.id,
    severity: severityVonEvent(ev.severity),
    title: ev.summary,
    detail: ev.entity,
    ts: ev.ts,
    href: eventHref(ev),
    domain: ev.domain,
    eventType: ev.type,
  };
  if (selbst) {
    // Abgeschlossene Episode: Anfang und Ende stehen beide fest.
    alert.seit = ev.ts;
    alert.bis = ev.acked_at;
    alert.selbstErledigt = true;
  } else if (ev.zustand && !ev.acked_at) {
    // Läuft noch. `ts` ist der ANFANG der Störung, auch bei
    // zusammengefassten Ereignissen: audit.Emit bumpt beim Wiederholen
    // nur noch `last_seen`. Das Detail zeigt daraus "weg seit …" — und
    // eine Störung, die seit gestern steht, sagt das jetzt auch, statt
    // sich stündlich als "vor 30 s" neu zu erfinden (Tim 31.08.).
    alert.seit = ev.ts;
    alert.zuletzt = ev.last_seen;
  }
  return alert;
}

// ── Vom Ereignis-Strom zur Liste ─────────────────────────────────────────────

/** fasseVerlauf macht aus den Ereignissen der letzten Tage die Rückblick-
 *  Liste: eine Zeile je Vorfall, neueste zuerst. Eine Störung, die sich
 *  geklärt hat, trägt Anfang UND Ende bei sich (eventToAlert liest das
 *  aus dem Auto-Ack des Servers) -- der Rückblick beantwortet damit die
 *  Frage, die man hat: „wann war es weg und wann wieder da".
 *
 *  Die Entwarnungen selbst stehen hier nicht: sie werden gar nicht mehr
 *  geladen. Eine gute Nachricht als eigene Zeile, die man wegklicken
 *  muss, ist genau die Abstumpfung, die die Liste unbrauchbar macht. */
export function fasseVerlauf(events: AppEvent[]): Alert[] {
  return events
    .map(eventToAlert)
    .sort((a, b) => (b.ts ?? "").localeCompare(a.ts ?? ""));
}

/** fasseZustaende macht aus den offenen Ereignissen die Meldungs-Liste.
 *  Offen heißt: der Server hat sie nicht geacked, also ist die Sache
 *  weder geklärt noch gelesen. */
export function fasseZustaende(events: AppEvent[]): Alert[] {
  // Kritisches gehört HINEIN. Bis 13.09.2026 stand hier ein Filter mit
  // der Begründung "kritische Fehler haben ihre eigene Lane" -- die Lane
  // ist aber nur der rote Banner auf dem Dashboard. Auf der Meldungen-
  // Seite und im unteren Panel fehlte damit ausgerechnet das Schlimmste:
  // Tim zog eine Platte aus dem RAIDZ2, der Pool stand DEGRADED, und
  // unter Meldungen hieß es "Keine offenen Warnungen". Das rote Symbol
  // in der Statusleiste öffnete dieselbe leere Liste.
  return events.map(eventToAlert);
}

/** meldungenLage: der Lage-Satz der Meldungen-Seite (docs/gestaltung.md,
 *  03.10.2026). Offen: je Stärke gezählt, die schwerste vorn und mit
 *  ihrer Farbe; leer ist „keine offenen Meldungen" mit Häkchen. Verlauf:
 *  wie viele Episoden, wie viele davon sich selbst erledigt haben (leise).
 *  Texte kommen fertig herein (t() an der Aufrufstelle). */
export function meldungenLage(
  rows: (Pick<Alert, "severity"> & { selbstErledigt?: boolean; acked?: boolean })[],
  ansicht: "active" | "history",
  text: (s: AlertSeverity, n: number) => string,
): { text: string; ton?: "ok" | "warn" | "danger" }[] {
  if (ansicht === "history") {
    if (!rows.length) return [{ text: "keine Meldungen in 7 Tagen", ton: "ok" }];
    const selbst = rows.filter((r) => r.selbstErledigt).length;
    const teile: { text: string; ton?: "ok" | "warn" | "danger" }[] = [{ text: `${rows.length} ${rows.length === 1 ? "Meldung" : "Meldungen"}` }];
    if (selbst) teile.push({ text: `${selbst} selbst erledigt` });
    return teile;
  }
  if (!rows.length) return [{ text: "keine offenen Meldungen", ton: "ok" }];
  const reihenfolge: AlertSeverity[] = ["critical", "problem", "warning", "info"];
  const teile: { text: string; ton?: "ok" | "warn" | "danger" }[] = [];
  for (const s of reihenfolge) {
    const n = rows.filter((r) => r.severity === s).length;
    if (!n) continue;
    const ton = SEVERITY_TON[s];
    teile.push({ text: text(s, n), ton: ton === "info" ? undefined : ton });
  }
  return teile;
}
